'use client';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import * as RDropdown from '@radix-ui/react-dropdown-menu';
import { cn } from '../../lib/cn';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { useInsideModal, usePortalContainer } from '../../lib/portal-container';
import { sanitizeInlineHtml } from '../../lib/sanitize-inline-html';
import { withDisplayName } from '../../lib/display-name';
import { SIcon, type SIconName } from '../SIcon';
import { buttonPreset, type SButtonColor } from '../SButton/button.config';

export type SDropdownButtonSize = 'xs' | 'sm' | 'md';

export interface SDropdownButtonItem {
  value: string | number;
  label: string;
  icon?: SIconName;
  disabled?: boolean;
}

export interface SDropdownButtonHandle {
  /** 드롭다운 열기 (sdOpen) */
  open: () => void;
  /** 드롭다운 닫기 (sdClose) */
  close: () => void;
}

export interface SDropdownButtonProps {
  /** 버튼 색상 */
  color?: SButtonColor;
  /** 아웃라인(테두리) 스타일 */
  outline?: boolean;
  /** 크기 (lg 미지원) */
  size?: SDropdownButtonSize;
  /** 버튼 레이블 */
  label?: string;
  /** 메뉴 아이템 목록 */
  items?: SDropdownButtonItem[];
  /** split 모드 — 레이블 버튼과 토글 분리 */
  split?: boolean;
  /** 비활성 */
  disabled?: boolean;
  /** 메뉴 아이템 선택 (sdClick) */
  onItemClick?: (value: string | number) => void;
  /** split 레이블 버튼 클릭 (sdButtonClick) */
  onLabelClick?: () => void;
  /** 열림/닫힘 변경 (sdDropDownShow) */
  onOpenChange?: (open: boolean) => void;
  className?: string;
  style?: CSSProperties;
}

// 드롭다운 전용 치수 (sd-dropdown-button.config: DROPDOWN_BUTTON_MIN_WIDTH / size 토큰)
const SIZE: Record<
  SDropdownButtonSize,
  {
    height: number;
    paddingX: number;
    gap: number;
    arrow: number;
    fontSize: number;
    minWidth: number;
    splitMinWidth: number;
  }
> = {
  xs: { height: 24, paddingX: 8, gap: 6, arrow: 12, fontSize: 12, minWidth: 64, splitMinWidth: 72 },
  sm: {
    height: 28,
    paddingX: 12,
    gap: 8,
    arrow: 12,
    fontSize: 12,
    minWidth: 72,
    splitMinWidth: 88,
  },
  md: {
    height: 36,
    paddingX: 20,
    gap: 16,
    arrow: 16,
    fontSize: 16,
    minWidth: 100,
    splitMinWidth: 106,
  },
};

/** split 모드 구분선 색 — (color, outline) 조합별 */
function splitDivider(color: SButtonColor, outline: boolean): string {
  const solid: Record<SButtonColor, string> = {
    primary: 'var(--cmp-button-dropdown-brand-strong-split)',
    secondary: 'var(--cmp-button-dropdown-brand-subtle-split)',
    neutral: 'var(--cmp-button-neutral-outline-border)',
    danger: 'var(--cmp-button-dropdown-danger-strong-split)',
  };
  const outlined: Record<SButtonColor, string> = {
    primary: 'var(--cmp-button-brand-outline-border)',
    secondary: 'var(--cmp-button-dropdown-brand-subtle-split)',
    neutral: 'var(--cmp-button-neutral-outline-border)',
    danger: 'var(--cmp-button-danger-outline-border)',
  };
  return (outline ? outlined : solid)[color];
}

// 원본(sd-dropdown-button.config) 메뉴 토큰 소스에 맞춤:
// default=fg.secondary / active bg=bg.accent.default / active 글자=fg.inverse / disabled=button.text.disabled
const itemClass = cn(
  'flex min-h-[28px] cursor-pointer select-none items-center gap-[8px] rounded-[2px] px-[12px] py-[4px] text-[12px] leading-[20px] outline-none',
  'text-[color:var(--sys-color-fg-secondary)]',
  'data-[disabled]:cursor-not-allowed data-[disabled]:text-[color:var(--cmp-button-text-disabled)]',
  'data-[highlighted]:bg-[var(--sys-color-bg-accent)] data-[highlighted]:text-[color:var(--sys-color-fg-inverse)]',
);

/**
 * SDropdownButton — sd-dropdown-button 포팅 (Radix DropdownMenu).
 * 원본처럼 자체 트리거를 렌더(sd-button 재사용 X): 드롭다운 전용 min-width + space-between 배치.
 */
const SDropdownButtonImpl = forwardRef<SDropdownButtonHandle, SDropdownButtonProps>(
  function SDropdownButton(
    {
      color = 'primary',
      outline = false,
      size = 'sm',
      label = '',
      items = [],
      split = false,
      disabled = false,
      onItemClick,
      onLabelClick,
      onOpenChange,
      className,
      style,
    }: SDropdownButtonProps,
    ref,
  ) {
    const portalContainer = usePortalContainer();
    const insideModal = useInsideModal();
    const [open, setOpen] = useState(false);
    // split 모드: RDropdown.Trigger가 우측 토글 버튼에만 걸려있어 align="start" 기준점 ·
    // --radix-dropdown-menu-trigger-width 모두 토글 버튼 기준이 된다. 드롭다운을 전체
    // 버튼(레이블+토글) 좌측·너비에 맞추기 위해 레이블 버튼 폭만큼 음수 alignOffset을 주고
    // Content 너비를 wrapper 전체 너비로 직접 지정한다 (Radix DropdownMenu는 Popover와 달리
    // 별도 Anchor를 지원하지 않아 이렇게 우회).
    const splitWrapRef = useRef<HTMLDivElement>(null);
    const splitToggleRef = useRef<HTMLButtonElement>(null);
    const [splitAlignOffset, setSplitAlignOffset] = useState(0);
    const [splitWidth, setSplitWidth] = useState(0);
    useEffect(() => {
      if (!split) return;
      const update = () => {
        if (splitWrapRef.current && splitToggleRef.current) {
          setSplitAlignOffset(
            -(splitWrapRef.current.offsetWidth - splitToggleRef.current.offsetWidth),
          );
          setSplitWidth(splitWrapRef.current.offsetWidth);
        }
      };
      update();
      const ro = new ResizeObserver(update);
      ro.observe(splitWrapRef.current!);
      return () => ro.disconnect();
    }, [split, label]);
    useImperativeHandle(
      ref,
      () => ({
        open: () => setOpen(true),
        close: () => setOpen(false),
      }),
      [],
    );
    const s = SIZE[size];
    const p = buttonPreset(color, outline);

    // 색상 CSS 변수 (hover 클래스가 정상 적용되도록)
    const colorVars = {
      '--dd-bg': disabled ? 'var(--cmp-button-bg-disabled)' : p.bg,
      '--dd-bg-hover': p.bgHover,
      '--dd-border': disabled ? 'var(--cmp-button-border-disabled)' : p.border,
      '--dd-text': disabled ? 'var(--cmp-button-text-disabled)' : p.text,
      '--dd-icon': disabled ? 'var(--cmp-button-icon-disabled)' : p.icon,
    } as CSSProperties;

    // non-split: caretDown (열림 시 회전) / split: etc 아이콘 고정 (회전 X)
    const caret = (
      <SIcon
        name="caretDown"
        size={s.arrow}
        color="var(--dd-icon)"
        className={cn('transition-transform', open && 'rotate-180')}
      />
    );
    const splitIcon = <SIcon name="etc" size={s.arrow} color="var(--dd-icon)" />;

    const triggerBase = cn(
      'box-border inline-flex items-center whitespace-nowrap border border-solid outline-none transition-colors',
      'border-[color:var(--dd-border)] bg-[var(--dd-bg)] text-[color:var(--dd-text)]',
      disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-[var(--dd-bg-hover)]',
    );

    const menu = (
      <RDropdown.Portal container={portalContainer}>
        <RDropdown.Content
          sideOffset={4}
          align="start"
          alignOffset={split ? splitAlignOffset : 0}
          className={cn(
            'z-50 overflow-hidden rounded-[var(--cmp-listBox-radius)] bg-[var(--cmp-listBox-bg)] shadow-normal-sm',
            !split && 'min-w-[var(--radix-dropdown-menu-trigger-width)]',
            FLOATING_SLIDE_ANIM,
          )}
          style={split ? { minWidth: splitWidth } : undefined}
        >
          {items.map(it => (
            <RDropdown.Item
              key={it.value}
              disabled={it.disabled}
              onSelect={() => onItemClick?.(it.value)}
              className={itemClass}
            >
              {it.icon && <SIcon name={it.icon} size={12} />}
              {/* 원본 sd-dropdown-button: label 을 살균 후 innerHTML 로 렌더 */}
              <span
                className="min-w-0 whitespace-nowrap"
                dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(it.label) }}
              />
            </RDropdown.Item>
          ))}
        </RDropdown.Content>
      </RDropdown.Portal>
    );

    return (
      <RDropdown.Root
        // Radix DropdownMenu 는 기본이 modal 이라 열려 있는 동안 body 와 (모달 안이라면)
        // Dialog Content 까지 pointer-events:none 이 된다. 그러면 모달 안의 다른 컨트롤을
        // 누를 때 히트테스트가 그 아래 백드롭으로 내려가, 메뉴만 닫히는 게 아니라 백드롭
        // 클릭으로 처리돼 persistent 모달이 흔들리고 누른 컨트롤은 열리지도 않는다.
        // 모달 안에서는 모달이 이미 모달리티를 갖고 있으므로 메뉴는 non-modal 로 둔다.
        modal={!insideModal}
        open={open}
        onOpenChange={o => {
          setOpen(o);
          onOpenChange?.(o);
        }}
      >
        {split ? (
          <div
            ref={splitWrapRef}
            className={cn('inline-flex items-stretch', className)}
            style={{ minWidth: s.splitMinWidth, ...style }}
          >
            {/* 레이블 파트 (flex-1 로 컨테이너 min-width 채움) */}
            <button
              type="button"
              disabled={disabled}
              onClick={onLabelClick}
              className={cn(triggerBase, 'flex-1 justify-center rounded-l-[4px] border-r-0')}
              style={{
                minHeight: s.height,
                padding: `0 ${s.paddingX}px`,
                fontSize: s.fontSize,
                ...colorVars,
              }}
            >
              {label}
            </button>
            {/* 토글(화살표) 파트 — 좌측 보더 하나만 divider (label은 우측 보더 제거) */}
            <RDropdown.Trigger asChild>
              <button
                ref={splitToggleRef}
                type="button"
                disabled={disabled}
                aria-label="메뉴 열기"
                className={cn(triggerBase, 'justify-center rounded-r-[4px] border-l')}
                style={{
                  minHeight: s.height,
                  minWidth: s.height,
                  borderLeftColor: disabled
                    ? 'var(--cmp-button-border-disabled)'
                    : splitDivider(color, outline),
                  ...colorVars,
                }}
              >
                {splitIcon}
              </button>
            </RDropdown.Trigger>
          </div>
        ) : (
          <RDropdown.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(triggerBase, 'justify-between rounded-[4px]', className)}
              // 라벨은 남은 폭을 채워 가운데 정렬 / 화살표는 우측 고정 + 드롭다운 전용 min-width
              style={{
                minWidth: s.minWidth,
                minHeight: s.height,
                padding: `0 ${s.paddingX}px`,
                gap: s.gap,
                fontSize: s.fontSize,
                ...colorVars,
                ...style,
              }}
            >
              <span className="flex-1 truncate text-center">{label}</span>
              {caret}
            </button>
          </RDropdown.Trigger>
        )}
        {menu}
      </RDropdown.Root>
    );
  },
);

export const SDropdownButton = withDisplayName(SDropdownButtonImpl, 'SDropdownButton');
